import React, { useState, useRef, memo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Users, Heart, Globe, GraduationCap, School, Calendar, Eye, Layers, Compass, Sparkles, MessageCircle, ChevronRight, Building2, ShieldCheck, Mail, Send } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Shared visual primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-6291">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-6292">
    <Eyebrow data-id="element-6293">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-6294">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-6295">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-6296" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-6297">
        {intro}
      </p>}
  </div>;
const PremiumCard = ({
  icon: Icon,
  title,
  body,
  highlight = false
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  highlight?: boolean;
}) => <div className={`group relative border ${highlight ? 'border-amber-400/40' : 'border-amber-400/15'} bg-gradient-to-b from-amber-950/10 to-transparent hover:border-amber-400/50 transition-colors duration-500 p-6 sm:p-7`} data-id="element-6298">
    <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6299" />
    <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-6300">
      <Icon className="w-4 h-4 text-amber-400" data-id="element-6301" />
    </div>
    <h3 className="font-serif text-lg sm:text-xl text-white mb-3 leading-tight" data-id="element-6302">
      {title}
    </h3>
    <p className="text-sm sm:text-[15px] text-white/60 leading-relaxed font-light" data-id="element-6303">
      {body}
    </p>
  </div>;
// =====================================================================
// 1. HERO
// =====================================================================
const SchoolsHero = () => <section className="relative w-full min-h-[92vh] bg-black flex items-center justify-center overflow-hidden pt-20" data-id="element-6304">
    <div className="absolute inset-0" data-id="element-6305">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-25" data-id="element-6306" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-6307" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-6308" />
    </div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-6309">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.1
    }} data-id="element-6310">
        <Link to="/" className="inline-flex items-center gap-2 text-amber-400/60 hover:text-amber-400 text-xs tracking-[0.25em] uppercase font-light mb-12 transition-colors" data-id="element-6311">
          <ArrowLeft className="w-3.5 h-3.5" data-id="element-6312" />
          Back to Life in the Sikh Kingdoms
        </Link>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.25
    }} data-id="element-6313">
        <p className="text-amber-400 tracking-[0.35em] text-[10px] sm:text-xs uppercase mb-7 font-light" data-id="element-6314">
          A School &amp; Curriculum Partnership Opportunity
        </p>
        <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white mb-7 tracking-tight leading-[0.95]" data-id="element-6315">
          For Schools &amp;
          <br data-id="element-6316" />
          <span className="text-amber-400" data-id="element-6317">Curriculum Partners</span>
        </h1>
        <div className="w-24 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-6318" />
        <p className="text-lg sm:text-xl md:text-2xl text-white/75 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-6319">
          An immersive Sikh history learning experience that supports identity,
          belonging, cultural understanding, and student engagement.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.55
    }} className="mt-10 max-w-3xl mx-auto" data-id="element-6320">
        <p className="text-sm sm:text-base text-white/55 font-light leading-relaxed" data-id="element-6321">
          Life of the Sikh Kingdoms brings Sikh history to life through
          immersive storytelling, visual world-building, and culturally
          meaningful learning. It offers schools a modern way to help middle
          school and high school students connect with history, heritage, and
          identity in a format that feels vivid, memorable, and relevant.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-6322">
        <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-6323">
          Request a Conversation
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6324" />
        </a>
        <a href="#program" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6325">
          Explore the School Program
        </a>
      </motion.div>
    </div>

    <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{
    y: [0, 10, 0]
  }} transition={{
    duration: 2,
    repeat: Infinity
  }} data-id="element-6326">
      <div className="w-px h-12 bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" data-id="element-6327" />
    </motion.div>
  </section>;
// =====================================================================
// 2. WHY THIS MATTERS
// =====================================================================
const WhyThisMattersSection = () => {
  const cards = [{
    icon: Heart,
    title: 'Identity & Belonging',
    body: 'A space where Punjabi Sikh students see their history, heritage, and people centred in a way that feels alive and contemporary.'
  }, {
    icon: BookOpen,
    title: 'Sikh History Made Meaningful',
    body: 'A visual, story-led entry point into Sikh history that moves beyond textbooks and feels relevant to today\u2019s students.'
  }, {
    icon: Globe,
    title: 'Cultural Understanding for All Students',
    body: 'For non-Sikh students, a thoughtful introduction to Sikh history, culture, and the communities they share classrooms with.'
  }];
  return <section id="program" className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6328">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-6329" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6330">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6331">
          <SectionHeading eyebrow="The Case for Schools" title="Why This" highlight="Matters" intro="Many students encounter history as something distant, abstract, or disconnected from their lives. Life of the Sikh Kingdoms offers a more meaningful entry point — deepening heritage and representation for Punjabi Sikh students, and broadening cultural understanding for everyone in the classroom." data-id="element-6332" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-6333">
          {cards.map((c, i) => <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} data-id="element-6334">
              <PremiumCard {...c} data-id="element-6335" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 3. WHO IT SERVES
// =====================================================================
const WhoItServesSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6336">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6337">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="text-center max-w-4xl mx-auto mb-14" data-id="element-6338">
        <Eyebrow data-id="element-6339">Who It Serves</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6340">
          Especially Meaningful for Punjabi Sikh Students —{' '}
          <span className="text-amber-400" data-id="element-6341">Valuable for All Students</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-6342" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-6343">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 to-transparent p-8 sm:p-10" data-id="element-6344">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6345" />
          <div className="flex items-center gap-3 mb-5" data-id="element-6346">
            <div className="w-10 h-10 border border-amber-400/50 bg-amber-400/10 flex items-center justify-center" data-id="element-6347">
              <Heart className="w-4 h-4 text-amber-400" data-id="element-6348" />
            </div>
            <Eyebrow data-id="element-6349">Heritage Centred</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-amber-400 mb-4 leading-tight" data-id="element-6350">
            For Punjabi Sikh Students
          </h3>
          <p className="text-white/70 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6351">
            Supports stronger connection to heritage, identity, pride, and
            cultural memory through a format that feels immersive and alive —
            history they can see themselves inside.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="relative border border-amber-400/15 bg-gradient-to-br from-white/[0.02] to-transparent p-8 sm:p-10" data-id="element-6352">
          <div className="flex items-center gap-3 mb-5" data-id="element-6353">
            <div className="w-10 h-10 border border-white/20 bg-white/5 flex items-center justify-center" data-id="element-6354">
              <Globe className="w-4 h-4 text-white/80" data-id="element-6355" />
            </div>
            <Eyebrow data-id="element-6356">Broader Learning</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 leading-tight" data-id="element-6357">
            For All Students
          </h3>
          <p className="text-white/65 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6358">
            Builds cultural understanding, empathy, and Sikh history literacy —
            helping students better understand the communities, histories, and
            stories around them.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 4. MIDDLE / HIGH SCHOOL
// =====================================================================
const GradeLevelSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6359">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6360">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="mb-14" data-id="element-6361">
        <SectionHeading eyebrow="Grade Levels" title="Designed for" highlight="Middle School and High School Learners" intro="The experience is built for both middle school and high school student groups — strong visual storytelling, emotional accessibility, and meaningful historical entry points across age ranges." data-id="element-6362" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-6363">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6364">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6365">
            <School className="w-5 h-5 text-amber-400" data-id="element-6366" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6367">
              GRADES 6–8
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6368">
            Middle School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6369">
            A vivid and memorable introduction to Sikh history, culture, and
            identity — built to spark curiosity and connection at an
            age-appropriate level.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6370">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6371">
            <GraduationCap className="w-5 h-5 text-amber-400" data-id="element-6372" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6373">
              GRADES 9–12
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6374">
            High School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6375">
            A deeper opportunity for reflection, discussion, leadership themes,
            and historical understanding — supporting humanities, history, and
            equity learning.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 5. WHAT STUDENTS GAIN
// =====================================================================
const WhatStudentsGainSection = () => {
  const items = [{
    icon: Compass,
    title: 'Stronger Identity & Belonging',
    body: 'A space where students see themselves and their communities reflected in the story.'
  }, {
    icon: Eye,
    title: 'Sikh History Experienced Vividly',
    body: 'Story-led, visual learning that makes history feel alive rather than abstract.'
  }, {
    icon: Heart,
    title: 'Cultural Understanding & Empathy',
    body: 'A clearer, more human view of Sikh history and the communities around them.'
  }, {
    icon: Sparkles,
    title: 'Engagement Beyond the Textbook',
    body: 'A learning format that feels modern, immersive, and memorable for students.'
  }, {
    icon: MessageCircle,
    title: 'Reflection &amp; Discussion',
    body: 'Meaningful prompts for in-class conversation, follow-up, and connection.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6376">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6377">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6378">
          <SectionHeading eyebrow="Student Outcomes" title="What Students" highlight="Gain" data-id="element-6379" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-6380">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={i === 4 ? 'lg:col-start-2' : ''} data-id="element-6381">
              <PremiumCard {...it} data-id="element-6382" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 6. HOW SCHOOLS CAN PARTICIPATE
// =====================================================================
const HowSchoolsParticipateSection = () => {
  const ways = [{
    icon: Users,
    title: 'School Group Visits',
    body: 'Organized middle school and high school visits to the immersive experience, structured around group size and learning goals.'
  }, {
    icon: Eye,
    title: 'Educator Preview Sessions',
    body: 'Preview opportunities for school leaders, curriculum teams, and educators to experience the program before bringing students.'
  }, {
    icon: BookOpen,
    title: 'Curriculum Enrichment',
    body: 'A meaningful experience that complements Sikh history, heritage learning, and broader multicultural education.'
  }, {
    icon: ShieldCheck,
    title: 'Pilot Partnerships',
    body: 'Early partnership opportunities for schools and education leaders interested in exploring access, fit, and scale.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6383">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-6384" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6385">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6386">
          <SectionHeading eyebrow="Participation" title="How Schools" highlight="Can Participate" data-id="element-6387" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-6388">
          {ways.map((w, i) => <motion.div key={w.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors p-7 flex gap-5 bg-gradient-to-br from-amber-950/5 to-transparent" data-id="element-6389">
              <span className="font-mono text-amber-400/40 text-xs tracking-[0.2em] shrink-0 pt-1" data-id="element-6390">
                0{i + 1}
              </span>
              <div className="flex-1" data-id="element-6391">
                <div className="flex items-center gap-3 mb-3" data-id="element-6392">
                  <w.icon className="w-4 h-4 text-amber-400" data-id="element-6393" />
                  <h3 className="font-serif text-lg sm:text-xl text-white leading-tight" data-id="element-6394">
                    {w.title}
                  </h3>
                </div>
                <p className="text-white/60 text-[14px] sm:text-[15px] font-light leading-relaxed" data-id="element-6395">
                  {w.body}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 7. CURRICULUM CONNECTION
// =====================================================================
const CurriculumConnectionSection = () => {
  const themes = ['Sikh history and heritage', 'Leadership and empire', 'Culture, art, and memory', 'Identity and continuity', 'Belonging and representation', 'Historical empathy', 'Cross-cultural learning'];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6396">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6397">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-6398">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-3" data-id="element-6399">
            <Eyebrow data-id="element-6400">Curriculum &amp; Learning</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6401">
              Built for Enrichment, Reflection, and{' '}
              <span className="text-amber-400" data-id="element-6402">Deeper Learning</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mb-6" data-id="element-6403" />
            <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed mb-5" data-id="element-6404">
              Life of the Sikh Kingdoms is designed to support heritage
              learning, Sikh history exploration, identity-based reflection, and
              broader multicultural understanding.
            </p>
            <p className="text-white/55 text-sm sm:text-base font-light leading-relaxed" data-id="element-6405">
              It is intended to complement school and community learning by
              making history feel vivid, human, and memorable — designed to{' '}
              <span className="text-amber-400/90" data-id="element-6406">support</span>,{' '}
              <span className="text-amber-400/90" data-id="element-6407">complement</span>, and{' '}
              <span className="text-amber-400/90" data-id="element-6408">enrich</span> existing
              programming.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="lg:col-span-2 relative border border-amber-400/20 bg-amber-950/5 p-6 sm:p-7" data-id="element-6409">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6410" />
            <Eyebrow data-id="element-6411">Learning Themes</Eyebrow>
            <ul className="mt-5 space-y-3" data-id="element-6412">
              {themes.map((t, i) => <li key={t} className="flex items-start gap-3 text-white/70 text-sm font-light" data-id="element-6413">
                  <span className="font-mono text-amber-400/60 text-[10px] tracking-[0.25em] mt-1 shrink-0" data-id="element-6414">
                    0{i + 1}
                  </span>
                  <span data-id="element-6415">{t}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// 8. DIGITAL EXTENSION (kept short & secondary)
// =====================================================================
const DigitalExtensionSection = () => <section className="py-20 md:py-24 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6416">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6417">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-3xl mx-auto text-center" data-id="element-6418">
        <Eyebrow data-id="element-6419">Optional Extension</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-5 mb-5 leading-[1.15] tracking-tight" data-id="element-6420">
          Optional Digital Engagement{' '}
          <span className="text-amber-400" data-id="element-6421">Beyond the Visit</span>
        </h2>
        <div className="w-12 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-6422" />
        <p className="text-white/60 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6423">
          An optional digital engagement layer can help students continue
          exploring Sikh history after the visit — through guided discovery,
          participation, and contemporary forms of engagement. Available as a
          supportive extension to the core in-person experience, not a
          replacement for it.
        </p>
        <div className="mt-7 inline-flex items-center gap-2 px-4 py-1.5 border border-amber-400/20 text-amber-400/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-6424">
          <Layers className="w-3 h-3" data-id="element-6425" />
          Optional · Secondary to In-Person Experience
        </div>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 9. COMMUNITY / MENTORSHIP
// =====================================================================
const CommunityMentorshipSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6426">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-6427" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-6428">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-4xl mx-auto text-center" data-id="element-6429">
        <Eyebrow data-id="element-6430">Beyond the Classroom</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6431">
          Learning That Can Extend{' '}
          <span className="text-amber-400" data-id="element-6432">Beyond the Experience</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto mb-7" data-id="element-6433" />
        <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-6434">
          This work can also open the door to broader community connection,
          mentorship, and intergenerational learning. It creates opportunities
          for students to encounter history not only as content, but as
          something connected to people, memory, and cultural leadership in the
          present.
        </p>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 10. WHO WE WORK WITH
// =====================================================================
const WhoWeWorkWithSection = () => {
  const audiences = [{
    icon: Compass,
    label: 'Curriculum Leaders'
  }, {
    icon: Building2,
    label: 'Principals &amp; Vice Principals'
  }, {
    icon: BookOpen,
    label: 'Educators'
  }, {
    icon: ShieldCheck,
    label: 'Trustees'
  }, {
    icon: Heart,
    label: 'Equity &amp; Inclusion Teams'
  }, {
    icon: Users,
    label: 'Student Engagement Teams'
  }, {
    icon: Globe,
    label: 'Community Education Partners'
  }, {
    icon: GraduationCap,
    label: 'Sikh &amp; Punjabi Education Advocates'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6435">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6436">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-6437">
          <SectionHeading eyebrow="Our Partners" title="Who We" highlight="Hope to Work With" data-id="element-6438" />
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto" data-id="element-6439">
          {audiences.map((a, i) => <motion.div key={a.label} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 bg-gradient-to-br from-amber-950/5 to-transparent p-5 transition-colors group" data-id="element-6440">
              <a.icon className="w-4 h-4 text-amber-400/70 group-hover:text-amber-400 mb-3 transition-colors" data-id="element-6441" />
              <p className="font-serif text-[15px] sm:text-base text-white leading-snug" dangerouslySetInnerHTML={{
            __html: a.label
          }} data-id="element-6442" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 11. CALL TO ACTION
// =====================================================================
const SchoolsCTASection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0.6]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-24 md:py-32 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6443">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.12),_transparent_60%)] pointer-events-none" data-id="element-6444" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6445">
        <div className="max-w-3xl mx-auto text-center" data-id="element-6446">
          <Eyebrow data-id="element-6447">Take the Next Step</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-7 leading-[1.05] tracking-tight" data-id="element-6448">
            Bring Sikh History{' '}
            <span className="text-amber-400" data-id="element-6449">to Life for Students</span>
          </h2>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-6450" />
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-6451">
            We are looking to work with schools, curriculum partners, and
            education leaders who want to create more meaningful, engaging, and
            culturally grounded learning experiences for middle school and high
            school students.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-6452">
            <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-6453">
              Request a School Conversation
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6454" />
            </a>
            <a href="#interest-form" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6455">
              Invite Us to Present
            </a>
          </div>
        </div>
      </div>
    </motion.section>;
};
// =====================================================================
// 12. INTEREST FORM
// =====================================================================
const InterestFormSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    school: '',
    role: '',
    grade: 'both',
    email: '',
    phone: '',
    interest: '',
    conversation: 'preview',
    comments: ''
  });
  const update = (k: string, v: string) => setForm(prev => ({
    ...prev,
    [k]: v
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const Field = ({
    label,
    children,
    optional = false
  }: {
    label: string;
    children: React.ReactNode;
    optional?: boolean;
  }) => <label className="block" data-id="element-6456">
      <span className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase mb-2 block" data-id="element-6457">
        {label}
        {optional && <span className="text-white/30 normal-case tracking-normal ml-2" data-id="element-6458">
            (optional)
          </span>}
      </span>
      {children}
    </label>;
  const inputCls = 'w-full bg-black border border-amber-400/20 px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-amber-400/60 transition-colors';
  return <section id="interest-form" className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6459">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6460">
        <div className="max-w-3xl mx-auto" data-id="element-6461">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-12" data-id="element-6462">
            <Eyebrow data-id="element-6463">Get in Touch</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-tight" data-id="element-6464">
              Share Your <span className="text-amber-400" data-id="element-6465">Interest</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-6466" />
            <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto leading-relaxed" data-id="element-6467">
              Tell us about your school or organization. We'll follow up with
              the right next step — whether that's a preview, a presentation, or
              a partnership conversation.
            </p>
          </motion.div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="relative border border-amber-400/40 bg-amber-950/10 p-10 sm:p-12 text-center" data-id="element-6468">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6469" />
              <div className="w-12 h-12 mx-auto border border-amber-400/50 bg-amber-400/10 flex items-center justify-center mb-5" data-id="element-6470">
                <Send className="w-5 h-5 text-amber-400" data-id="element-6471" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-3" data-id="element-6472">
                Thank you — we'll be in touch
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed max-w-md mx-auto" data-id="element-6473">
                Your interest has been received. A member of the school
                partnerships team will reach out shortly to set up the right
                next conversation.
              </p>
            </motion.div> : <motion.form onSubmit={handleSubmit} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative border border-amber-400/20 bg-gradient-to-b from-amber-950/8 to-transparent p-7 sm:p-10 space-y-6" data-id="element-6474">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6475" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6476">
                <Field label="Name" data-id="element-6477">
                  <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" className={inputCls} data-id="element-6478" />
                </Field>
                <Field label="Role" data-id="element-6479">
                  <input type="text" required value={form.role} onChange={e => update('role', e.target.value)} placeholder="e.g. Principal, Curriculum Lead" className={inputCls} data-id="element-6480" />
                </Field>
              </div>

              <Field label="School / Organization" data-id="element-6481">
                <input type="text" required value={form.school} onChange={e => update('school', e.target.value)} placeholder="School or district name" className={inputCls} data-id="element-6482" />
              </Field>

              <Field label="Grade Level of Interest" data-id="element-6483">
                <div className="flex flex-wrap gap-2" data-id="element-6484">
                  {[{
                v: 'middle',
                l: 'Middle School'
              }, {
                v: 'high',
                l: 'High School'
              }, {
                v: 'both',
                l: 'Both'
              }].map(opt => {
                const active = form.grade === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('grade', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-6485">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6486">
                <Field label="Email" data-id="element-6487">
                  <input type="email" required value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@school.org" className={inputCls} data-id="element-6488" />
                </Field>
                <Field label="Phone" optional data-id="element-6489">
                  <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (___) ___-____" className={inputCls} data-id="element-6490" />
                </Field>
              </div>

              <Field label="Area of Interest" data-id="element-6491">
                <input type="text" value={form.interest} onChange={e => update('interest', e.target.value)} placeholder="e.g. heritage learning, equity programming, school visit" className={inputCls} data-id="element-6492" />
              </Field>

              <Field label="What would you like first?" data-id="element-6493">
                <div className="flex flex-wrap gap-2" data-id="element-6494">
                  {[{
                v: 'preview',
                l: 'Educator Preview'
              }, {
                v: 'presentation',
                l: 'Presentation'
              }, {
                v: 'partnership',
                l: 'Partnership Conversation'
              }].map(opt => {
                const active = form.conversation === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('conversation', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-6495">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <Field label="Comments" optional data-id="element-6496">
                <textarea rows={4} value={form.comments} onChange={e => update('comments', e.target.value)} placeholder="Anything you'd like us to know — context, timing, group size, etc." className={inputCls + ' resize-none'} data-id="element-6497" />
              </Field>

              <button type="submit" className="group w-full inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-6498">
                <Mail className="w-4 h-4" data-id="element-6499" />
                Send to School Partnerships
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6500" />
              </button>

              <p className="text-white/35 text-[10px] tracking-[0.2em] uppercase text-center" data-id="element-6501">
                We'll respond within 3 business days
              </p>
            </motion.form>}
        </div>
      </div>
    </section>;
};
// =====================================================================
// PAGE
// =====================================================================
export function SchoolsPage() {
  return <LanguageProvider data-id="element-6502">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-6503">
        <ScrollProgress data-id="element-6504" />
        <Navbar data-id="element-6505" />
        <main className="w-full" data-id="element-6506">
          <SchoolsHero data-id="element-6507" />
          <WhyThisMattersSection data-id="element-6508" />
          <WhoItServesSection data-id="element-6509" />
          <GradeLevelSection data-id="element-6510" />
          <WhatStudentsGainSection data-id="element-6511" />
          <HowSchoolsParticipateSection data-id="element-6512" />
          <CurriculumConnectionSection data-id="element-6513" />
          <DigitalExtensionSection data-id="element-6514" />
          <CommunityMentorshipSection data-id="element-6515" />
          <WhoWeWorkWithSection data-id="element-6516" />
          <SchoolsCTASection data-id="element-6517" />
          <InterestFormSection data-id="element-6518" />
        </main>
        <Footer data-id="element-6519" />
      </div>
    </LanguageProvider>;
}